import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Draft, original } from "immer";
import { isEqual } from "lodash";

import {
  CHART_FOLD_KEYS,
  ChartExploreChannelManyPerSeries,
  ChartExploreChannelOnePerSeries,
  ChartExploreChannelTopLevel,
  ExploreChannel,
  ExploreChartConfig,
  ExploreChartSeries,
  ExploreDetailField,
  ExploreField,
  ExploreFieldId,
  ExploreFieldSimpleOptions,
  ExploreFieldType,
  ExploreSeriesId,
  ExploreSpec,
  ExploreTimeUnit,
  ExploreUserJoin,
  ExploreUserJoinColumn,
  ExploreUserJoinTable,
  ExploreViewType,
  PivotExploreChannel,
  SemanticAwareColumn,
  SemanticAwareFieldGroup,
  calciteTypeisDatetimeType,
  calciteTypeisNumericType,
  crossAxisAcceptsWide,
  defaultExploreAggregation,
  defaultExploreChartSeries,
  exploreSpecToPivotConfig,
  generateColumnIdForField,
  isHistogram,
  mapExploreFieldsToChartFields,
  mapExploreFieldsToPivotFields,
  maybeInvalidMappingReason,
  pivotAggToHqlAgg,
  pivotTruncUnitToExploreTruncUnit,
  updateExploreSpecFromChartDiffs,
} from "../";
import { dataFrameHeaderTypeToChartDataType } from "../../chart/chartDataTypeUtils.js";
import { ChartSpec, ChartTypeSelectorOption } from "../../chart/types.js";
import { assertNever } from "../../errors.js";
import {
  TEMPORAL_CALCITE_TYPES,
  columnTypeToCalciteType,
} from "../../hql/types.js";
import {
  DisplayTableColumnId,
  SemanticDatasetName,
} from "../../idTypeBrands.js";
import { PivotGroupByField, PivotValueField } from "../../pivot/pivotTypes.js";
import { getDropValueForPivotSection as getPivotDropValue } from "../../pivot/pivotUiUtils.js";
import {
  SemanticDatasetStub,
  getJoinsToDelete,
} from "../../semantic-layer/semanticGraph.js";
import { PushdownSqlDialect } from "../../sql/dialects.js";
import { uuid } from "../../uuid.js";
import { getExploreSimpleType } from "../exploreAggregationUtils.js";
import { ExploreInputField } from "../exploreDndTypes.js";
import {
  AggregableChannel,
  LumpChannel,
  TruncationChannel,
} from "../exploreFieldMenuUtils.js";

// #region main explore state modification logic

const exploreSpecSlice = createSlice({
  name: "exploreSpec",
  initialState: {} as unknown as ExploreSpec,
  reducers: {
    setSpec(_state, { payload: spec }: PayloadAction<ExploreSpec>) {
      return spec;
    },
    /**
     * Adds the input field, auto-populating chart properties like channel and aggregation
     * and creating a new series if necessary.
     */
    autoAddField(
      state,
      {
        payload: { inputField },
      }: PayloadAction<{
        inputField: ExploreInputField;
      }>,
    ) {
      addDefaultSeriesIfNone(state);
      const firstSeries = state.chartConfig.series[0]!;
      const { fieldType } = inputField;
      const { aggregation, channel } = getAutoAddFieldDestination(
        state,
        inputField,
      );

      // Apply common sort order if all fields in the channel have the same sort
      const commonSort = getCommonSortOrderForChannel(state.fields, channel);

      updateOrAddField(state, {
        id: inputField.fieldId ?? ExploreFieldId.check(uuid()),
        seriesId: firstSeries.id,
        channel,
        aggregation,
        title: inputField.id !== inputField.name ? inputField.name : undefined,
        dataType: columnTypeToCalciteType(inputField.type),
        value: inputField.id,
        fieldType: fieldType,
        queryPath: inputField.queryPath,
        ...(commonSort != null && { sort: commonSort }),
      });
      cleanUpOrphanedSeries(state);
    },
    addChartTopLevelField(
      state,
      {
        payload: { channel, inputField, options },
      }: PayloadAction<{
        channel: ChartExploreChannelTopLevel;
        inputField: ExploreInputField;
        options?: UpdateOrAddFieldOptions;
      }>,
    ) {
      addDefaultSeriesIfNone(state);

      // There can only be one of each type of top-level field,
      // so if we're adding a new one, remove any previous ones
      state.fields = state.fields.filter((f) => f.channel !== channel);

      const newFieldData: ExploreField = {
        id: inputField.fieldId ?? ExploreFieldId.check(uuid()),
        seriesId: state.chartConfig.series[0]!.id,
        channel,
        title: inputField.id !== inputField.name ? inputField.name : undefined,
        dataType: columnTypeToCalciteType(inputField.type),
        value: inputField.id,
        fieldType: inputField.fieldType,
        queryPath: inputField.queryPath,
        truncUnit: inputField.truncUnit ?? options?.truncUnit ?? undefined,
        scaleType: options?.scaleType,
        lump: inputField.lump,
      };

      updateOrAddField(state, newFieldData, {
        ...options,
        clearAggregationOnUpdate: true,
      });
      cleanUpOrphanedSeries(state);

      if (
        channel === "h-facet" &&
        state.fields.find((f) => f.channel === "v-facet") != null &&
        state.chartConfig.facet?.columns != null
      ) {
        // special case
        // clear h-wrap so we don't immediately error on h drop
        // in the presence of v
        state.chartConfig.facet.columns = undefined;
      }
    },
    addChartSeriesField(
      state,
      {
        payload: {
          aggregation,
          channel,
          inputField,
          options,
          seriesId: seriesId_,
        },
      }: PayloadAction<{
        channel:
          | ChartExploreChannelOnePerSeries
          | ChartExploreChannelManyPerSeries;
        inputField: ExploreInputField;
        /** If not included, defaults to the first series */
        seriesId?: ExploreSeriesId;
        /**
         * Note: you must specifically set this to null to not use a default aggregation
         */
        aggregation?: ExploreField["aggregation"] | null;
        options?: UpdateOrAddFieldOptions;
      }>,
    ) {
      addDefaultSeriesIfNone(state);
      const seriesId = seriesId_ ?? state.chartConfig.series[0]!.id;

      let resolvedAggregation;
      if (inputField.fieldType === ExploreFieldType.MEASURE) {
        resolvedAggregation = undefined;
      } else if (aggregation != null) {
        resolvedAggregation = aggregation;
        // eslint-disable-next-line eqeqeq -- explicit null check where null is intent to ignore default agg
      } else if (aggregation === null) {
        resolvedAggregation = undefined;
      } else {
        resolvedAggregation = defaultExploreAggregation(channel, inputField);
      }
      //TODO(EXPLORE) centralize these field-creation blocks
      const newFieldData: ExploreField = {
        id: inputField.fieldId ?? ExploreFieldId.check(uuid()),
        seriesId,
        channel,
        title: inputField.id !== inputField.name ? inputField.name : undefined,
        dataType: columnTypeToCalciteType(inputField.type),
        value: inputField.id,
        aggregation: resolvedAggregation,
        fieldType: inputField.fieldType,
        queryPath: inputField.queryPath,
        scaleType: options?.scaleType,
        lump: LumpChannel.guard(channel) ? inputField.lump : undefined,
      };

      updateOrAddField(state, newFieldData, options);
      cleanUpOrphanedSeries(state);
    },
    addPivotField(
      state,
      {
        payload: { channel, dialect, inputField, options },
      }: PayloadAction<{
        inputField: ExploreInputField;
        channel: PivotExploreChannel;
        dialect: PushdownSqlDialect;
        options?: UpdateOrAddFieldOptions;
      }>,
    ) {
      addDefaultSeriesIfNone(state);
      const firstSeriesId = state.chartConfig.series[0]!.id;
      const optionsWithIdx = {
        ...options,
        channelIdx: inputField.idx,
      };
      const pivotSection = `${channel}s` as const;
      const pivotValue = getPivotDropValue({
        item: { field: inputField.id },
        fieldType: inputField.type,
        section: pivotSection,
        dialect,
        currentAggregation: inputField.aggregation,
        config: exploreSpecToPivotConfig(state),
      });

      // Check to see if the field is already in the spec and it's safe to reuse
      const fieldBeingMoved = state.fields.find(
        (f) => f.id === inputField.fieldId,
      );

      let newFieldData: ExploreField;
      const isGroupbyChannel = (ch: string): boolean =>
        ch === "row" || ch === "column";

      const fieldIsSafeToReuse =
        fieldBeingMoved != null &&
        isGroupbyChannel(fieldBeingMoved.channel) === isGroupbyChannel(channel);

      if (fieldIsSafeToReuse) {
        newFieldData = {
          ...fieldBeingMoved,
          channel, // in case we're moving a row to col or vice versa
        };
      } else {
        //TODO(EXPLORE) centralize these field-creation blocks
        newFieldData = {
          id: inputField.fieldId ?? ExploreFieldId.check(uuid()),
          seriesId: firstSeriesId,
          channel,
          title:
            inputField.id !== inputField.name ? inputField.name : undefined,
          value: inputField.id,
          dataType: columnTypeToCalciteType(inputField.type),
          displayFormat: pivotValue.displayFormat,
          truncUnit: inputField.truncUnit,
          queryPath: inputField.queryPath,
          fieldType: inputField.fieldType,
          scaleType: options?.scaleType,
          lump: inputField.lump,
        };

        // Apply common sort order if all fields in the channel have the same sort
        const commonSort = getCommonSortOrderForChannel(state.fields, channel);
        if (commonSort != null) {
          newFieldData.sort = commonSort;
        }

        if (PivotValueField.guard(pivotValue)) {
          //TODO(EXPLORE): EXP-1210 - our generated pivotValue will set a default
          //aggregation value, so we just ignore it for measures. We can remove
          //this one we update the pivot config to support measures
          newFieldData.aggregation =
            inputField.fieldType !== ExploreFieldType.MEASURE
              ? pivotAggToHqlAgg(pivotValue.aggregation)
              : undefined;
        } else {
          newFieldData.aggregation = undefined;
        }
        if (
          PivotGroupByField.guard(pivotValue) &&
          newFieldData.truncUnit == null
        ) {
          newFieldData.truncUnit = pivotTruncUnitToExploreTruncUnit(
            pivotValue.truncateTo,
          );
        }
      }
      updateOrAddField(state, newFieldData, optionsWithIdx);
      cleanUpOrphanedSeries(state);
    },
    removeField(
      state,
      { payload: { fieldId } }: PayloadAction<{ fieldId: ExploreFieldId }>,
    ) {
      state.fields = state.fields.filter((f) => f.id !== fieldId);
      onRemoveField(state);
      cleanUpOrphanedSeries(state);
    },
    removeSeries(
      state,
      { payload: { seriesId } }: PayloadAction<{ seriesId: ExploreSeriesId }>,
    ) {
      const seriesFieldIdsToRemove = new Set(
        state.fields
          .filter(
            (f) =>
              f.seriesId === seriesId &&
              (ChartExploreChannelOnePerSeries.guard(f.channel) ||
                f.channel === "cross-axis"),
          )
          .map((f) => f.id),
      );
      state.fields = state.fields.filter(
        (f) => !seriesFieldIdsToRemove.has(f.id),
      );

      const seriesTooltipFieldsToMove = state.fields.filter(
        (field) => field.seriesId === seriesId && field.channel === "tooltip",
      );
      const nextAvailableSeriesId = state.chartConfig.series.filter(
        (series) => series.id !== seriesId,
      )?.[0]?.id;

      // move tooltips to the next available series or remove if no other series
      if (nextAvailableSeriesId) {
        seriesTooltipFieldsToMove.forEach((field) => {
          field.seriesId = nextAvailableSeriesId;
        });
      } else {
        const tooltipFieldIdsToRemove = new Set(
          seriesTooltipFieldsToMove.map((f) => f.id),
        );
        state.fields = state.fields.filter(
          (f) => !tooltipFieldIdsToRemove.has(f.id),
        );
      }

      onRemoveField(state);
      cleanUpOrphanedSeries(state);
    },
    removeMatchingFields(
      state,
      {
        payload: { channel, field },
      }: PayloadAction<{
        field:
          | Pick<ExploreField, "value" | "queryPath" | "aggregation">
          | SemanticAwareColumn
          | { id: string; queryPath: SemanticDatasetName[] };
        // Optional: only remove fields from this channel
        channel?: ExploreChannel;
      }>,
    ) {
      const fieldId = generateColumnIdForField(field);
      const fieldAgg =
        "aggregation" in field
          ? field.aggregation
          : "columnAggregation" in field
            ? field.columnAggregation
            : undefined;

      state.fields = state.fields.filter((f) => {
        const matchesChannel = !channel || f.channel === channel;
        const isMatching =
          generateColumnIdForField(f) === fieldId &&
          f.aggregation === fieldAgg &&
          matchesChannel;
        return !isMatching;
      });

      onRemoveField(state);
      cleanUpOrphanedSeries(state);
    },
    removeAllMatchingFields(
      state,
      { payload: { values } }: PayloadAction<{ values: string[] }>,
    ) {
      state.fields = state.fields.filter(
        (f) => !values.includes(generateColumnIdForField(f)),
      );
      state.details.fields = state.details.fields.filter(
        (f) => !values.includes(generateColumnIdForField(f)),
      );

      onRemoveField(state);
      cleanUpOrphanedSeries(state);
    },

    replaceField(
      state,
      {
        payload: { fieldToReplaceId, newField, options },
      }: PayloadAction<{
        fieldToReplaceId: ExploreFieldId;
        newField: ExploreInputField;
        options?: UpdateOrAddFieldOptions;
      }>,
    ) {
      const oldField = state.fields.find((f) => f.id === fieldToReplaceId);
      if (!oldField) {
        return;
      }

      // Replace the new field at the same index as the old field
      const oldFieldIdx = state.fields
        .filter((f) => f.channel === oldField.channel)
        .findIndex((f) => f.id === fieldToReplaceId);
      state.fields = state.fields.filter((f) => f.id !== fieldToReplaceId);

      // If we're dragging (moving) an already-active field to replace another active field,
      // delete the old one
      state.fields = state.fields.filter((f) => f.id !== newField.fieldId);

      const newFieldData: ExploreField = {
        id: ExploreFieldId.check(uuid()),
        seriesId: oldField.seriesId,
        channel: oldField.channel,
        title: newField.id !== newField.name ? newField.name : undefined,
        value: newField.id,
        dataType: columnTypeToCalciteType(newField.type),
        displayFormat: oldField.displayFormat,
        queryPath: newField.queryPath,
        fieldType: newField.fieldType,
        truncUnit:
          options?.truncUnit ??
          (TEMPORAL_CALCITE_TYPES.includes(
            columnTypeToCalciteType(newField.type),
          )
            ? oldField.truncUnit
            : undefined),
        scaleType: options?.scaleType,
      };

      const isAggregableChannel = AggregableChannel.guard(oldField.channel);
      const isMeasure = newField.fieldType === ExploreFieldType.MEASURE;
      const isSameSimpleType =
        getExploreSimpleType(newFieldData, state.visualizationType) ===
        getExploreSimpleType(oldField, state.visualizationType);
      newFieldData.aggregation = isAggregableChannel
        ? newField.aggregation ??
          (!isMeasure && isSameSimpleType && oldField.aggregation !== undefined
            ? oldField.aggregation
            : defaultExploreAggregation(oldField.channel, newField))
        : undefined;

      updateOrAddField(state, newFieldData, {
        channelIdx: oldFieldIdx,
      });
      cleanUpOrphanedSeries(state);
    },
    updateCalcName(
      state,
      {
        payload: { currName, newName },
      }: PayloadAction<{
        currName: DisplayTableColumnId;
        newName?: DisplayTableColumnId;
      }>,
    ) {
      state.fields
        .filter((f) => f.value === currName)
        .forEach((existingField) => {
          const newFieldData = {
            ...existingField,
            value: newName ?? existingField.value,
          };
          updateOrAddField(state, newFieldData);
        });

      state.details.fields = state.details.fields.map((f) =>
        f.value === currName ? { ...f, value: newName ?? f.value } : f,
      );
      cleanUpOrphanedSeries(state);
    },
    /**
     * A simple update for field properties which can just be directly set by the user.
     * More complex transformations, like assigning a field to a new series,
     * should be handled by a method of their own.
     */
    setFieldProperty(
      state,
      {
        payload: { fieldId, updateData },
      }: PayloadAction<{
        fieldId: ExploreFieldId;
        updateData: Partial<ExploreFieldSimpleOptions>;
      }>,
    ) {
      const field = state.fields.find((f) => f.id === fieldId);
      if (field == null) {
        return;
      }
      Object.assign(field, updateData);

      // If updating sort on a CHART_FOLD_KEYS field, sync it across base-axis/color CHART_FOLD_KEYS fields
      if (updateData.sort != null && field.value === CHART_FOLD_KEYS) {
        const foldKeysFieldsToSync = state.fields.filter(
          (f) =>
            f.value === CHART_FOLD_KEYS &&
            f.seriesId === field.seriesId &&
            f.id !== fieldId &&
            ["base-axis", "color"].includes(f.channel),
        );
        foldKeysFieldsToSync.forEach((f) => {
          f.sort = updateData.sort;
        });
      }
    },
    /**
     * Certain cross-axis properties need to be kept in sync
     * across fields in the same series group.
     */
    setFieldPropertyAndSyncCrossAxis(
      state,
      {
        payload: { fieldId, updateData },
      }: PayloadAction<{
        fieldId: ExploreFieldId;
        updateData: Pick<
          ExploreField,
          "scaleType" | "displayFormat" | "truncUnit"
        >;
      }>,
    ) {
      const field = state.fields.find((f) => f.id === fieldId);
      if (field == null) {
        return;
      }
      let fieldsToUpdate = [field];
      const resolvedUpdateData: Partial<ExploreFieldSimpleOptions> = updateData;
      if (field.channel === "cross-axis") {
        const seriesGroup = state.chartConfig.seriesGroups.find((group) =>
          group.includes(field.seriesId),
        );
        if (seriesGroup != null) {
          fieldsToUpdate = state.fields.filter(
            (f) =>
              f.channel === "cross-axis" && seriesGroup.includes(f.seriesId),
          );
        }

        // if changing to a non-numeric scale type, clear out any aggs
        if (
          updateData.scaleType !== undefined &&
          updateData.scaleType !== "number"
        ) {
          resolvedUpdateData.aggregation = undefined;
        }
      }
      fieldsToUpdate.forEach((f) => {
        Object.assign(f, resolvedUpdateData);
      });
    },
    enableDetailFields(
      state,
      {
        payload: { baseTableFields },
      }: PayloadAction<{
        baseTableFields: SemanticAwareColumn[];
      }>,
    ) {
      state.details.enabled = true;
      if (state.details.fields.length === 0) {
        state.details.showAllBaseTableDetailFields = true;
        state.details.fields = baseTableFields.map(
          semanticAwareColumnToDetailField,
        );
      }
    },
    disableDetailFields(state, _: PayloadAction<void>) {
      state.details.enabled = false;
    },
    addDetailField(
      state,
      {
        payload: { allFieldsInGroup, field },
      }: PayloadAction<{
        field: SemanticAwareColumn;
        allFieldsInGroup: SemanticAwareColumn[];
      }>,
    ) {
      const isBaseTable = field.queryPath.length === 0;

      // if we are adding a base table field and we're supposed to be showing
      // all base table fields, but actually do not have any base table fields
      // add them all to the details table
      // handle this snowflake case
      if (
        isBaseTable &&
        state.details.showAllBaseTableDetailFields &&
        state.details.fields.length === 0 &&
        state.fields.length === 0
      ) {
        state.details.fields = allFieldsInGroup.map(
          semanticAwareColumnToDetailField,
        );
        state.details.enabled = true;
        return;
      }

      const existingDetailFieldIds = new Set(
        state.details.fields.map(generateColumnIdForField),
      );
      if (existingDetailFieldIds.has(generateColumnIdForField(field))) {
        return;
      }

      // adding field shows intent to see detail columns in table
      // so enable details table
      state.details.enabled = true;
      state.details.fields.push(semanticAwareColumnToDetailField(field));

      if (isBaseTable) {
        state.details.showAllBaseTableDetailFields =
          detailFieldsHaveAllBaseTableFields(
            state.details.fields,
            allFieldsInGroup,
          );
      }
    },
    removeDetailField(
      state,
      {
        payload: { baseTableFields, field },
      }: PayloadAction<{
        field: SemanticAwareColumn | Pick<ExploreField, "value" | "queryPath">;
        /**
         * Base table fields *must* be provided in the event that a single
         * field is removed when `showAllBaseTableDetailFields` is true.
         */
        baseTableFields: SemanticAwareColumn[];
      }>,
    ) {
      if (
        state.details.showAllBaseTableDetailFields &&
        state.details.fields.length === 0
      ) {
        state.details.fields = baseTableFields.map(
          semanticAwareColumnToDetailField,
        );
      }

      const removedFieldId = generateColumnIdForField(field);
      state.details.fields = state.details.fields.filter(
        (f) => generateColumnIdForField(f) !== removedFieldId,
      );
      state.details.showAllBaseTableDetailFields =
        detailFieldsHaveAllBaseTableFields(
          state.details.fields,
          baseTableFields,
        );
    },
    removeCalc(state, payloadAction: PayloadAction<{ value: string }>) {
      const {
        payload: { value },
      } = payloadAction;
      state.fields = state.fields.filter(
        (f) => generateColumnIdForField(f) !== value,
      );
      state.details.fields = state.details.fields.filter(
        (f) => generateColumnIdForField(f) !== value,
      );

      onRemoveField(state);
      cleanUpOrphanedSeries(state);
    },
    // used for settting base table fields without enabling details
    setBaseTableDetailFields(
      state,
      {
        payload: { baseTableFields },
      }: PayloadAction<{ baseTableFields: SemanticAwareColumn[] }>,
    ) {
      state.details.fields = baseTableFields.map(
        semanticAwareColumnToDetailField,
      );
    },
    showAllBaseTableDetailFields(
      state,
      {
        payload: { baseTableFields },
      }: PayloadAction<{ baseTableFields: SemanticAwareColumn[] }>,
    ) {
      const existingDetailFieldIds = new Set(
        state.details.fields.map(generateColumnIdForField),
      );
      for (const f of baseTableFields) {
        if (!existingDetailFieldIds.has(generateColumnIdForField(f))) {
          state.details.fields.push(semanticAwareColumnToDetailField(f));
        }
      }

      state.details.enabled = true;
      state.details.showAllBaseTableDetailFields = true;
    },
    removeAllDetailFields(state) {
      state.details.fields = [];
      state.details.showAllBaseTableDetailFields = false;
    },
    clearFields(state) {
      state.fields = [];
      onRemoveField(state);
      cleanUpOrphanedSeries(state);
    },
    setViewType(state, { payload: viewType }: PayloadAction<ExploreViewType>) {
      state.viewType = viewType;
    },
    setVizType(
      state,
      {
        payload: vizType,
      }: PayloadAction<
        ChartTypeSelectorOption | "pivot-table" | "histogram_normalized"
      >,
    ) {
      if (vizType === "pivot-table") {
        state.visualizationType = "pivot-table";
        state.fields = mapExploreFieldsToPivotFields(state.fields);
        addDefaultSeriesIfNone(state);
        state.chartConfig.series = [state.chartConfig.series[0]!];
        const firstSeriesId = state.chartConfig.series[0]!.id;
        state.chartConfig.seriesGroups = [[firstSeriesId]];
        state.fields.forEach((f) => (f.seriesId = firstSeriesId));
      } else {
        setChartTypeOrSeriesType(state, vizType);
      }
      // note: do not call cleanUpOrphanedSeries() here, as we either do it
      // in the pivot branch above, else we only operate on single series

      // if looking at table only, switch to both so that the user
      // can see the visualization as well
      if (state.viewType === "source-table") {
        state.viewType = "both";
      }
    },
    setSeriesType(
      state,
      {
        payload: { seriesId, vizType },
      }: PayloadAction<{
        seriesId: ExploreSeriesId;
        vizType: ChartTypeSelectorOption | "histogram_normalized";
      }>,
    ) {
      setChartTypeOrSeriesType(state, vizType, seriesId);
    },
    setChartSpec(
      state,
      {
        payload: { fieldGroups, newChartSpec, oldChartSpec },
      }: PayloadAction<{
        oldChartSpec: ChartSpec;
        newChartSpec: ChartSpec;
        fieldGroups: SemanticAwareFieldGroup[] | undefined;
      }>,
    ) {
      // note: do not call cleanUpOrphanedSeries() here, as this function does
      // its own series-management
      return updateExploreSpecFromChartDiffs({
        curSpec: oldChartSpec,
        newSpec: newChartSpec,
        // `updateExploreSpecFromChartDiffs` clones the spec,
        // so we just pass it the original object instead of the immer object
        exploreSpec: original(state)!,
        fieldGroups,
      });
    },
    addUserJoin(
      state,
      { payload: { join, tables } }: PayloadAction<ExploreAddUserJoinPayload>,
    ) {
      state.joins ??= [];
      state.joins.push(join);

      // Any table with the same name that was previously configured is replaced by the new data.
      const tableNames = new Set(tables.map((t) => t.name));
      state.tables ??= [];
      state.tables = state.tables
        .filter((t) => !tableNames.has(t.name))
        .concat(tables);
    },
    editUserJoin(
      state,
      {
        payload: {
          basePrimaryKey,
          joinToEdit,
          relationshipType,
          sourceTableJoinColumn,
          targetPrimaryKey,
          targetTableJoinColumn,
        },
      }: PayloadAction<ExploreEditUserJoinPayload>,
    ) {
      const joinToEditIdx = state.joins?.findIndex((j) =>
        isEqual(original(j), joinToEdit),
      );
      if (
        joinToEditIdx == null ||
        state.joins == null ||
        state.tables == null
      ) {
        throw new Error("Join to edit not found");
      }
      const sourceTable = state.tables.find(
        (t) => t.name === joinToEdit.sourceTable.name,
      );
      const targetTable = state.tables.find(
        (t) => t.name === joinToEdit.targetTable.name,
      );
      if (sourceTable == null || targetTable == null) {
        throw new Error("Source or target table not found");
      }
      state.joins[joinToEditIdx] = {
        ...joinToEdit,
        sourceTableJoinColumn,
        targetTableJoinColumn,
        relationshipType,
      };
      sourceTable.primaryKeyColumn = basePrimaryKey;
      targetTable.primaryKeyColumn = targetPrimaryKey;
    },
    removeUserJoin(
      state,
      {
        payload: { baseTableName, targetTableName },
      }: PayloadAction<{
        baseTableName: SemanticDatasetName;
        targetTableName: SemanticDatasetName;
      }>,
    ) {
      if (state.joins == null) return;

      // Make set of faux semantic datasets so we can use our semantic graph helpers
      const datasets: { [name: string]: SemanticDatasetStub } = {};
      for (const join of state.joins) {
        const {
          sourceTable: { name: base },
          targetTable: { name: target },
        } = join;
        datasets[base] ??= {
          name: base,
          properties: { joins: [] },
        };
        datasets[base].properties.joins!.push({ target });
      }

      // Get all downstream joins that we need to remove in addition to orignal one
      const joinsToRemove = getJoinsToDelete(Object.values(datasets), [
        baseTableName,
        targetTableName,
      ]);

      // Remove all those joins
      state.joins = state.joins.filter((j) => {
        const base = j.sourceTable.name;
        const target = j.targetTable.name;
        return joinsToRemove[base] == null || !joinsToRemove[base].has(target);
      });

      // Remove any tables that are no longer included in the graph at all
      const remainingTables = new Set(
        state.joins.flatMap((j) => [j.sourceTable.name, j.targetTable.name]),
      );
      if (state.tables == null) return;
      state.tables = state.tables.filter((t) => remainingTables.has(t.name));

      // Only keep fields that reference still remaining tables
      state.fields = state.fields.filter(
        (f) =>
          f.queryPath.length === 0 ||
          f.queryPath.every((t) => remainingTables.has(t)),
      );
      state.details.fields = state.details.fields.filter(
        (f) =>
          f.queryPath.length === 0 ||
          f.queryPath.every((t) => remainingTables.has(t)),
      );
      cleanUpOrphanedSeries(state);
    },
    removeAllUserJoins(state) {
      const hasJoins = (state.joins ?? []).length > 0;
      const hasTables = (state.tables ?? []).length > 0;

      if (hasJoins || hasTables) {
        state.joins = [];
        state.tables = [];

        // Remove any fields which reference anything other than the base table
        // (This won't work correctly once we allow user-defined joins on semantic projects)
        state.fields = state.fields.filter((f) => f.queryPath.length === 0);
        state.details.fields = state.details.fields.filter(
          (f) => f.queryPath.length === 0,
        );
        cleanUpOrphanedSeries(state);
      }
    },
    setChartConfigProperty<
      // exclude series-related properties as changes to these properties must be made in tandem
      // with other explore fields
      K extends keyof Exclude<ExploreChartConfig, "series" | "seriesGroups">,
    >(
      state: Draft<ExploreSpec>,
      {
        payload: { key, value },
      }: PayloadAction<{
        key: K;
        value: ExploreChartConfig[K];
      }>,
    ) {
      state.chartConfig[key] = value;
    },
    setChartConfigSeriesProperty<K extends keyof Draft<ExploreChartSeries>>(
      state: Draft<ExploreSpec>,
      action: PayloadAction<{
        key: K;
        seriesId: ExploreSeriesId;
        value: Draft<ExploreChartSeries>[K];
      }>,
    ) {
      const { key, seriesId, value } = action.payload;
      const series = state.chartConfig.series.find((s) => s.id === seriesId);
      if (series) {
        series[key] = value;
      }
    },
    setAndSyncChartConfigSeriesProperty<
      K extends keyof Pick<ExploreChartSeries, "tooltip">,
    >(
      state: Draft<ExploreSpec>,
      {
        payload: { key, value },
      }: PayloadAction<{
        key: K;
        value: Partial<ExploreChartSeries[K]>;
      }>,
    ) {
      state.chartConfig.series.forEach((series) => {
        if (series[key] == null) {
          series[key] = value as ExploreChartSeries[K];
        } else {
          Object.assign(series[key], value);
        }
      });
    },

    updateValuesAsRows(
      state,
      { payload: { valuesAsRows } }: PayloadAction<{ valuesAsRows: boolean }>,
    ) {
      state.valuesAsRows = valuesAsRows;
    },

    updateRowTotals(
      state,
      { payload: { rowTotals } }: PayloadAction<{ rowTotals: boolean }>,
    ) {
      state.rowTotals = rowTotals;
    },

    updateColumnTotals(
      state,
      { payload: { columnTotals } }: PayloadAction<{ columnTotals: boolean }>,
    ) {
      state.columnTotals = columnTotals;
    },

    updateRowSubtotals(
      state,
      { payload: { rowSubtotals } }: PayloadAction<{ rowSubtotals: boolean }>,
    ) {
      state.rowSubtotals = rowSubtotals;
    },

    updateColumnSubtotals(
      state,
      {
        payload: { columnSubtotals },
      }: PayloadAction<{ columnSubtotals: boolean }>,
    ) {
      state.columnSubtotals = columnSubtotals;
    },

    addEmptySeries(
      state,
      {
        payload: { colorPalette, groupIndex },
      }: PayloadAction<{
        colorPalette: readonly string[];
        groupIndex: number;
      }>,
    ) {
      const newSeries: ExploreChartSeries = defaultExploreChartSeries("line");

      // Get first unused color for default color
      const usedColors = new Set<string>();
      const firstColor = colorPalette[0];
      state.chartConfig.series.forEach((series) => {
        if (series.color?.source === "color" && series.color.staticValue) {
          usedColors.add(series.color.staticValue);
        } else if (!series.color && firstColor) {
          usedColors.add(firstColor);
        }
      });

      const firstUnusedColor =
        colorPalette.find((color) => !usedColors.has(color)) ?? firstColor;

      newSeries.color = {
        source: "color",
        staticValue: firstUnusedColor,
      };

      state.chartConfig.series.push(newSeries);

      const targetGroup = state.chartConfig.seriesGroups[groupIndex];
      if (targetGroup) {
        targetGroup.push(newSeries.id);
      } else {
        state.chartConfig.seriesGroups[groupIndex] = [newSeries.id];
      }
    },
  },
});

// #endregion

// #region state modication helpers

export function getAutoAddFieldDestination(
  spec: Pick<ExploreSpec, "chartConfig" | "fields" | "visualizationType">,
  inputField: ExploreInputField,
): {
  channel: ExploreChannel;
  aggregation: "Sum" | undefined;
} {
  const firstSeries = spec.chartConfig.series[0];
  const firstSeriesChannels = spec.fields
    .filter((f) => f.seriesId === firstSeries!.id)
    .map((f) => f.channel);

  const { fieldType } = inputField;
  const numericCol = fieldType !== "MEASURE" && inputField.type === "NUMBER";
  if (
    spec.visualizationType === "chart" &&
    isHistogram(spec.chartConfig) &&
    numericCol &&
    !firstSeriesChannels.includes("base-axis")
  ) {
    return {
      channel: "base-axis",
      aggregation: undefined,
    };
  }

  const aggregation: "Sum" | undefined = numericCol ? "Sum" : undefined;
  const isMeasure = fieldType === "MEASURE" || aggregation != null;

  const findAvailableChannel = (
    candidates: ExploreChannel[],
  ): ExploreChannel => {
    for (const candidate of candidates) {
      if (
        !firstSeriesChannels.includes(candidate) &&
        maybeInvalidMappingReason({
          spec,
          channel: candidate,
          incomingType: dataFrameHeaderTypeToChartDataType(inputField.type),
          isAggregated: isMeasure,
          isFoldKeys: inputField.name === CHART_FOLD_KEYS,
        }) == null
      ) {
        return candidate;
      }
    }
    if (spec.visualizationType === "chart") {
      return "tooltip";
    } else if (spec.visualizationType === "pivot-table") {
      return isMeasure ? "value" : "row";
    } else {
      assertNever(spec.visualizationType, spec.visualizationType);
    }
  };

  const channel = isMeasure
    ? findAvailableChannel(["value", "cross-axis"])
    : findAvailableChannel(["row", "base-axis", "color", "h-facet", "v-facet"]);

  return {
    channel,
    aggregation,
  };
}

function addDefaultSeriesIfNone(state: Draft<ExploreSpec>): void {
  if (state.chartConfig.series.length === 0) {
    const newSeries = defaultExploreChartSeries();
    state.chartConfig.series.push(newSeries);
    state.chartConfig.seriesGroups = [[newSeries.id]];
  }
}

function onRemoveField(state: Draft<ExploreSpec>): void {
  if (state.fields.length === 0) {
    state.details.enabled = true;
    if (state.details.fields.length === 0) {
      state.details.showAllBaseTableDetailFields = true;
    }
  }
}

function updateOrAddField(
  state: Draft<ExploreSpec>,
  field: ExploreField,
  options?: UpdateOrAddFieldOptions,
): void {
  const existingFieldIdx = state.fields.findIndex((f) => f.id === field.id);
  const newIdx = options?.channelIdx;

  // If adding base-axis/color CHART_FOLD_KEYS field, adopt sort from existing base-axis/color CHART_FOLD_KEYS
  // do this first before we add the field
  if (
    field.value === CHART_FOLD_KEYS &&
    ["base-axis", "color"].includes(field.channel)
  ) {
    const existingSortableFoldKeysFields = state.fields.filter(
      (f) =>
        f.id !== field.id &&
        f.value === CHART_FOLD_KEYS &&
        f.seriesId === field.seriesId &&
        ["base-axis", "color"].includes(f.channel),
    );
    if (
      existingSortableFoldKeysFields.length > 0 &&
      existingSortableFoldKeysFields[0]?.sort != null
    ) {
      field.sort = existingSortableFoldKeysFields[0].sort;
    }
  }

  // If the field is already in the spec, "update" it
  if (existingFieldIdx !== -1) {
    const [existingField] = state.fields.splice(existingFieldIdx, 1);
    const globalIdx =
      newIdx != null
        ? getGlobalIndexForChannelPosition(state.fields, field.channel, newIdx)
        : existingFieldIdx;
    const updatedField = {
      ...existingField, // copy extant {displayFormat, bin, ...} that weren't overritten in the calling code
      // When a field is moved e.g. from cross-axis to base-axis, sometimes the aggregation sticks around
      ...(options?.clearAggregationOnUpdate ? { aggregation: undefined } : {}),
      ...field,
    };
    // NOTE: we end up copying stuff like truncUnit and bin from e.g. base-axis to tooltip here
    // and these are just ignored downstream

    state.fields.splice(globalIdx, 0, updatedField);

    // If moving the 2nd field away from cross-axis, remove any CHART_FOLD_KEYS fields
    if (
      existingField != null &&
      existingField.channel === "cross-axis" &&
      field.channel !== "cross-axis" &&
      crossAxisAcceptsWide(state.chartConfig, state.visualizationType)
    ) {
      const remainingCrossAxisFields = state.fields.filter(
        (f) =>
          f.channel === "cross-axis" && f.seriesId === existingField.seriesId,
      );

      if (remainingCrossAxisFields.length === 1) {
        state.fields = state.fields.filter(
          (f) =>
            !(
              f.value === CHART_FOLD_KEYS &&
              f.seriesId === existingField.seriesId
            ),
        );
      }
    }
  } else {
    if (state.fields.length === 0) {
      // When adding the first agg/group by field, we hide all detail fields
      state.details.enabled = false;
    }
    if (
      calciteTypeisDatetimeType(field.dataType) &&
      TruncationChannel.guard(field.channel) &&
      // eslint-disable-next-line eqeqeq -- Explicit `null` check (not undefined!) means don't pick a default truncation
      options?.truncUnit !== null
    ) {
      const lowercaseColumnName = field.value.toLowerCase();
      // Guess at increasing the default trunc unit
      const defaultTruncUnit = lowercaseColumnName.includes("day")
        ? "day"
        : lowercaseColumnName.includes("week")
          ? "week"
          : lowercaseColumnName.includes("year")
            ? "year"
            : lowercaseColumnName.includes("quarter")
              ? "quarter"
              : "month";
      field.truncUnit = field.truncUnit ?? defaultTruncUnit;
    }

    if (
      calciteTypeisNumericType(field.dataType) &&
      (field.channel === "h-facet" || field.channel === "v-facet")
    ) {
      field.bin = field.bin ?? { count: 6 };
    }

    // Set axis grid style to "none" for new fields in the second series group
    if (state.chartConfig.seriesGroups.length > 1) {
      const seriesGroupIndex = state.chartConfig.seriesGroups.findIndex(
        (group) => group.includes(field.seriesId),
      );
      if (seriesGroupIndex === 1) {
        field.axis = {
          ...field.axis,
          grid: { style: "none" },
        };
      }
    }

    if (newIdx != null) {
      const globalIdx = getGlobalIndexForChannelPosition(
        state.fields,
        field.channel,
        newIdx,
      );
      state.fields.splice(globalIdx, 0, field);
    } else if (options?.addToFront) {
      state.fields.unshift(field);
    } else {
      state.fields.push(field);
    }
  }

  if (
    field.channel === "cross-axis" &&
    crossAxisAcceptsWide(state.chartConfig, state.visualizationType)
  ) {
    // if adding a 2nd cross-axis field, automatically color by CHART_FOLD_KEYS if no color field already exists
    const crossAxisFields = state.fields.filter(
      (f) => f.channel === "cross-axis" && f.seriesId === field.seriesId,
    );
    const hasColorField = state.fields.some(
      (f) => f.channel === "color" && f.seriesId === field.seriesId,
    );

    if (crossAxisFields.length === 2 && !hasColorField) {
      const baseAxisFoldKeys = state.fields.find(
        (f) =>
          f.value === CHART_FOLD_KEYS &&
          f.seriesId === field.seriesId &&
          f.channel === "base-axis",
      );

      const sort = baseAxisFoldKeys?.sort ?? {
        mode: "custom-order",
        customOrder: crossAxisFields.map((f) => f.value),
      };

      state.fields.push({
        id: ExploreFieldId.check(uuid()),
        seriesId: field.seriesId,
        channel: "color",
        dataType: "VARCHAR",
        value: CHART_FOLD_KEYS,
        fieldType: "COLUMN",
        queryPath: [],
        sort,
      });
    }

    // and update CHART_FOLD_KEYS custom order to match current field order
    const sortableFoldKeysFields = state.fields.filter(
      (f) =>
        f.value === CHART_FOLD_KEYS &&
        f.seriesId === field.seriesId &&
        ["base-axis", "color"].includes(f.channel),
    );

    if (sortableFoldKeysFields.length > 0) {
      const customOrder = crossAxisFields.map((f) => f.value);
      sortableFoldKeysFields.forEach((f) => {
        f.sort = {
          mode: "custom-order",
          customOrder,
        };
      });
    }
  }
}

/**
 * Converts a channel-specific index to a global array position.
 *
 * The fields array contains all fields across all channels in a flat structure, but users in pivots
 * interact with fields organized by channel (rows, columns, values, etc.). This function maps
 * a position within a specific channel (e.g., "2nd field in the rows channel") to
 * the corresponding index in the global fields array.
 *
 * @param channelIdx - The desired position within the channel
 * @returns The global array index where the field should be inserted
 */
function getGlobalIndexForChannelPosition(
  fields: ExploreField[],
  channel: ExploreChannel,
  channelIdx: number,
): number {
  const channelFields = fields
    .map((field, globalIdx) => ({ field, globalIdx }))
    .filter(({ field }) => field.channel === channel);

  if (channelFields.length === 0) {
    return 0;
  }
  // If the channelIdx is greater than the number of fields in the channel, insert at the end
  if (channelIdx >= channelFields.length) {
    return channelFields[channelFields.length - 1]!.globalIdx + 1;
  }
  return channelFields[channelIdx]!.globalIdx;
}

/**
 * Shared logic for applying a chart type change to a series.
 * Used by both setVizType (chart-level) and setSeriesType (series-level).
 */
function setChartTypeOrSeriesType(
  state: Draft<ExploreSpec>,
  vizType: ChartTypeSelectorOption | "histogram_normalized",
  seriesId?: ExploreSeriesId,
): void {
  const previousVisType = state.visualizationType;
  state.visualizationType = "chart";

  if (state.chartConfig.series.length === 1 || seriesId != null) {
    const targetSeries = seriesId
      ? state.chartConfig.series.find((s) => s.id === seriesId)
      : state.chartConfig.series[0];

    if (!targetSeries) {
      return; // Series not found, do nothing
    }

    const previousType = seriesId
      ? targetSeries.type
      : previousVisType !== "chart"
        ? undefined
        : targetSeries.type;

    // Only update orientation for chart-level changes, not series-level
    if (seriesId == null) {
      state.chartConfig.orientation = "vertical"; // default, overridden for bars
    }

    switch (vizType) {
      case "column_grouped":
        targetSeries.type = "bar";
        targetSeries.normalize = undefined;
        targetSeries.barGrouped = true;
        if (seriesId == null) {
          state.chartConfig.orientation = "vertical";
        }
        break;
      case "bar_grouped":
        targetSeries.type = "bar";
        targetSeries.barGrouped = true;
        targetSeries.normalize = undefined;
        if (seriesId == null) {
          state.chartConfig.orientation = "horizontal";
        }
        break;
      case "column_stacked":
        targetSeries.type = "bar";
        targetSeries.barGrouped = false;
        targetSeries.normalize = undefined;
        if (seriesId == null) {
          state.chartConfig.orientation = "vertical";
        }
        break;
      case "bar_stacked":
        targetSeries.type = "bar";
        targetSeries.barGrouped = false;
        targetSeries.normalize = undefined;
        if (seriesId == null) {
          state.chartConfig.orientation = "horizontal";
        }
        break;
      case "column_stacked100":
        targetSeries.type = "bar";
        targetSeries.barGrouped = false;
        targetSeries.normalize = "base-axis";
        if (seriesId == null) {
          state.chartConfig.orientation = "vertical";
        }
        break;
      case "bar_stacked100":
        targetSeries.type = "bar";
        targetSeries.barGrouped = false;
        targetSeries.normalize = "base-axis";
        if (seriesId == null) {
          state.chartConfig.orientation = "horizontal";
        }
        break;
      case "area_stacked":
        targetSeries.type = "area";
        targetSeries.normalize = undefined;
        break;
      case "area_stacked100":
        targetSeries.type = "area";
        targetSeries.normalize = "base-axis";
        break;
      case "histogram":
        targetSeries.type = "histogram";
        targetSeries.normalize = undefined;
        break;
      case "histogram_normalized":
        targetSeries.type = "histogram";
        targetSeries.normalize = "facet";
        break;
      case "line":
        targetSeries.type = "line";
        break;
      case "scatter":
        targetSeries.type = "scatter";
        break;
      case "pie":
        targetSeries.type = "pie";
        break;
      default:
        assertNever(vizType, vizType);
    }

    // Only update fields for chart-level changes, not series-level changes
    if (seriesId == null) {
      const newFields = mapExploreFieldsToChartFields(state, previousType);
      if (
        newFields.find((f) => f.channel === "h-facet") &&
        newFields.find((f) => f.channel === "v-facet") &&
        state.chartConfig.facet?.columns != null
      ) {
        //special case: the mapper gave us h/v so we cannot wrap h
        state.chartConfig.facet.columns = undefined;
      }
      state.fields = newFields;
    }
  }
}

/**
 * If an existing field is moved or removed,
 * it's possible that there are now orphaned series.
 * Remove these if necessary.
 */
function cleanUpOrphanedSeries(state: Draft<ExploreSpec>): void {
  addDefaultSeriesIfNone(state);
  const origFirstSeries = state.chartConfig.series[0]!;

  // Make a set of all series without specific channels mapped to them
  const orphanedSeriesIds = new Set(state.chartConfig.series.map((s) => s.id));
  for (const field of state.fields) {
    if (
      ChartExploreChannelOnePerSeries.guard(field.channel) ||
      ChartExploreChannelManyPerSeries.guard(field.channel)
    ) {
      orphanedSeriesIds.delete(field.seriesId);
    }
  }

  // Remove those series
  //TODO(EXPLORE) we need to look at this function more closely...
  // series without fields aren't bugs, they're necessary for charts that are
  // in the process of being constructed
  state.chartConfig.series = state.chartConfig.series.filter(
    (s) => !orphanedSeriesIds.has(s.id),
  );

  // If we removed all the series, bring back the first one so we have something
  if (state.chartConfig.series.length === 0) {
    state.chartConfig.series.push(origFirstSeries);
    orphanedSeriesIds.delete(origFirstSeries.id);
  }

  // If top-level fields were assigned to a removed series, reassign them to the new first series
  if (orphanedSeriesIds.has(origFirstSeries.id)) {
    const newFirstSeriesId = state.chartConfig.series[0]!.id;
    for (const field of state.fields) {
      if (field.seriesId === origFirstSeries.id) {
        field.seriesId = newFirstSeriesId;
      }
    }
  }

  // Removed series need to be removed from the series group they were in
  // And then any empty groups should also be removed
  for (let i = 0; i < state.chartConfig.seriesGroups.length; i++) {
    const group = state.chartConfig.seriesGroups[i]!;
    state.chartConfig.seriesGroups[i] = group.filter((id) =>
      state.chartConfig.series.some((s) => s.id === id),
    );
  }
  state.chartConfig.seriesGroups = state.chartConfig.seriesGroups.filter(
    (group) => group.length > 0,
  );
}

function semanticAwareColumnToDetailField(
  column: SemanticAwareColumn,
): ExploreDetailField {
  return {
    value: column.columnId,
    queryPath: column.queryPath,
    dataType: columnTypeToCalciteType(column.columnType),
    fieldType: column.fieldType,
  };
}

function detailFieldsHaveAllBaseTableFields(
  detailFields: ExploreDetailField[],
  allFieldsInGroup: SemanticAwareColumn[],
): boolean {
  const existingDetailFieldIds = new Set(
    detailFields.map(generateColumnIdForField),
  );
  return allFieldsInGroup.every((f) =>
    existingDetailFieldIds.has(generateColumnIdForField(f)),
  );
}

// #endregion

// #region exports for external use
export type ExploreAddUserJoinPayload = {
  join: ExploreUserJoin;
  tables: ExploreUserJoinTable[];
};

export type ExploreEditUserJoinPayload = {
  basePrimaryKey: ExploreUserJoinColumn;
  targetPrimaryKey: ExploreUserJoinColumn;
  sourceTableJoinColumn: ExploreUserJoinColumn;
  targetTableJoinColumn: ExploreUserJoinColumn;
  relationshipType: ExploreUserJoin["relationshipType"];
  joinToEdit: ExploreUserJoin;
};

export interface UpdateOrAddFieldOptions {
  addToFront?: boolean;
  /**
   * If provided, the field will be added at the given index within the channel/section, or moved to it if it already exists
   * Note that this is NOT a global index in the fields array
   */
  channelIdx?: number;
  clearAggregationOnUpdate?: boolean;
  /**
   * NOTE: if you want to explicitly this to null to not use a default time truncation
   */
  truncUnit?: ExploreTimeUnit | null;
  scaleType?: ExploreField["scaleType"];
}

export const { actions: exploreSpecActions, reducer: exploreSpecReducer } =
  exploreSpecSlice;

type SliceActions<T extends { [k: string]: (...args: any[]) => unknown }> = {
  [k in keyof T]: ReturnType<T[k]>;
}[keyof T];
export type ExploreSpecUpdateActions = SliceActions<typeof exploreSpecActions>;

// #endregion

/**
 * Helper function to get the common sort order for all fields in a channel.
 * Returns the sort order if all fields in the channel have the same sort order,
 * otherwise returns undefined.
 */
function getCommonSortOrderForChannel(
  fields: ExploreField[],
  channel: ExploreField["channel"],
): ExploreField["sort"] | undefined {
  const sameChannelFields = fields.filter((f) => f.channel === channel);
  if (sameChannelFields.length > 0) {
    const firstSort = sameChannelFields[0]?.sort;
    const allHaveSameSort = sameChannelFields.every((f) =>
      isEqual(f.sort, firstSort),
    );
    if (allHaveSameSort && firstSort != null) {
      return firstSort;
    }
  }
  return undefined;
}
